import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './live.css';
import { getCanvasFormatById } from '../canvasFormats';
import { normalizePresetLayerOrder, layerIdToPrimitiveId } from '../presets/presetLayerUtils';
import { getEditorUrl, getLiveUrl, getOutputUrl } from './liveWindowManager';
import {
  getControlTokenFromHash,
  getRelayClockNow,
  getRelayOverrideFromSearch,
  getRoomIdFromSearch,
  LIVE_PHASE_PLAYING,
} from '../shared/liveProtocol';
import { ReactComponent as PlayStreamIcon } from '../assets/icons/live/play-stream.svg';
import { ReactComponent as PauseIcon } from '../assets/icons/live/pause.svg';
import { ReactComponent as MediaBackwardEndIcon } from '../assets/icons/live/media-backward-end.svg';
import { ReactComponent as StopIcon } from '../assets/icons/live/stop.svg';
import { createLivePlaylistItem } from '../state/livePlaylistStore';
import { useRelayConnection } from './useRelayConnection';
import { isProgramInOutro } from './livePlayback';
import PlaybackCanvas from './PlaybackCanvas';
import MirrorLeftIcon from '../icons/MirrorLeftIcon';

const DeleteIcon = () => (
  <svg className="layer-action-delete-icon live-playlist-delete-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 7H12H18V18C18 19.6569 16.6569 21 15 21H9C7.34315 21 6 19.6569 6 18V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoCircleIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 17V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

const OUTPUT_URL_TOOLTIP = 'Copy or drag&drop this into your software of choice';
const CONTROL_URL_TOOLTIP = 'Share this only with someone who should control the room';

const normalizeTextSource = (value, fallback = 'main') => (
  new Set(['main', 'sub', 'generic']).has(value) ? value : fallback
);

const normalizeLayerName = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const resolveGenericTextValue = ({ person, activePresetId, primitiveId, fallbackText }) => {
  const bucket = person?.genericTextByPresetId?.[activePresetId];
  if (!bucket || typeof bucket !== 'object') return fallbackText;
  return typeof bucket[primitiveId] === 'string' ? bucket[primitiveId] : fallbackText;
};

const getVisibleGenericLayers = (project) => {
  let genericIndex = 0;
  const shapePrimitives = Array.isArray(project?.config?.shapePrimitives) ? project.config.shapePrimitives : [];
  const shapeById = new Map(shapePrimitives.map((shape, index) => [shape?.id || `shape-${index}`, shape]));
  const fallbackLabels = new Map(
    shapePrimitives.map((shape, index) => {
      if (shape?.layerKind !== 'text') return null;
      if (normalizeTextSource(shape?.textSource, 'main') !== 'generic') return null;
      genericIndex += 1;
      return [shape?.id || `shape-${index}`, `Generic text ${genericIndex}`];
    }).filter(Boolean),
  );

  return normalizePresetLayerOrder(project?.config?.layerOrder, shapePrimitives)
    .map((layerId) => layerIdToPrimitiveId(layerId))
    .filter(Boolean)
    .map((primitiveId) => {
      const primitive = shapeById.get(primitiveId);
      if (!primitive || primitive.layerKind !== 'text') return null;
      if (normalizeTextSource(primitive.textSource, 'main') !== 'generic') return null;
      if (primitive.hideFromPeoplePanel === true) return null;
      return {
        primitiveId,
        label: normalizeLayerName(primitive.layerName) || fallbackLabels.get(primitiveId) || 'Generic text',
        fallbackText: typeof primitive.textValue === 'string' ? primitive.textValue : '',
      };
    })
    .filter(Boolean);
};

const getPlaylistItemEffectiveMirrored = (item, person) => (
  item?.mirroredOverride ?? person?.mirrored ?? false
);

const buildPlaylistRenderPerson = (item, person) => (
  item && person ? { ...person, mirrored: getPlaylistItemEffectiveMirrored(item, person) } : null
);

const insertPlaylistItemAt = (playlistItems, item, targetIndex) => {
  const next = [...playlistItems];
  next.splice(Math.max(0, Math.min(targetIndex, next.length)), 0, item);
  return next;
};

const movePlaylistItemToIndex = (playlistItems, playlistItemId, targetIndex) => {
  const currentIndex = playlistItems.findIndex((item) => item.id === playlistItemId);
  if (currentIndex === -1) return playlistItems;
  const next = [...playlistItems];
  const [movedItem] = next.splice(currentIndex, 1);
  const clampedIndex = Math.max(0, Math.min(targetIndex, next.length));
  next.splice(currentIndex < clampedIndex ? clampedIndex - 1 : clampedIndex, 0, movedItem);
  return next;
};

const formatPersonSubtitle = (person) => (person ? (person.title || ' ') : ' ');

const buildSelectedSubtitle = (person) => {
  if (!person) return 'No selection';
  return person.title ? `${person.name || 'Untitled'} - ${person.title}` : (person.name || 'Untitled');
};

const getRelayErrorMessage = (error) => {
  switch (error?.code) {
    case 'ROOM_NOT_FOUND':
      return 'This live room no longer exists. Go back to the editor and create a new live room.';
    case 'ROOM_EXPIRED':
      return 'This live room expired after being inactive. Go back to the editor and create a new one.';
    case 'UNAUTHORIZED':
      return 'This control link is missing a valid room token. Open the private control link from the editor again.';
    case 'INVALID_HELLO':
      return 'The relay rejected this live-control session. Reload the page or create a new room.';
    case 'FORBIDDEN_ORIGIN':
      return 'This deployed site is not allowed to use the live relay. Check your Railway allowed origins.';
    case 'RATE_LIMITED':
      return 'The live relay is rate-limiting this browser. Wait a moment and try again.';
    default:
      return error?.message || 'Unable to connect to the live relay for this room.';
  }
};

const renderEmptyState = ({ title, body, actionLabel = 'Back to Editor' }) => (
  <div className="live-shell live-shell-centered">
    <div className="live-panel-card live-empty-state">
      <h1>{title}</h1>
      <p>{body}</p>
      <button type="button" className="secondary-btn" onClick={() => window.open(getEditorUrl(), '_self')}>
        {actionLabel}
      </button>
    </div>
  </div>
);

const LiveControlApp = () => {
  const roomId = useMemo(() => getRoomIdFromSearch(typeof window === 'undefined' ? '' : window.location.search), []);
  const controlToken = useMemo(() => getControlTokenFromHash(typeof window === 'undefined' ? '' : window.location.hash), []);
  const relayOrigin = useMemo(() => getRelayOverrideFromSearch(typeof window === 'undefined' ? '' : window.location.search), []);
  const relay = useRelayConnection({ clientRole: 'control', roomId, controlToken });
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedSourcePersonId, setSelectedSourcePersonId] = useState(null);
  const [playbackNow, setPlaybackNow] = useState(() => Date.now());
  const lastPreviewItemIdRef = useRef(null);
  const project = relay.project;
  const live = relay.live;
  const people = useMemo(() => (Array.isArray(project?.people) ? project.people : []), [project]);
  const playlistItems = useMemo(
    () => (Array.isArray(live?.playlistItems) ? live.playlistItems : []),
    [live?.playlistItems],
  );
  const format = getCanvasFormatById(project?.aspectRatioId);
  const outputUrl = useMemo(() => (roomId ? getOutputUrl({ roomId, relayOrigin }) : ''), [relayOrigin, roomId]);
  const controlUrl = useMemo(
    () => (roomId ? getLiveUrl({ roomId, controlToken, relayOrigin }) : ''),
    [controlToken, relayOrigin, roomId],
  );
  const peopleById = useMemo(() => new Map(people.map((person) => [person.id, person])), [people]);
  const selectedPlaylistItem = useMemo(
    () => playlistItems.find((item) => item.id === live?.previewItemId) || null,
    [live?.previewItemId, playlistItems],
  );
  const playlistSelectedPerson = selectedPlaylistItem ? peopleById.get(selectedPlaylistItem.personId) || null : null;
  const selectedSourcePerson = selectedSourcePersonId ? peopleById.get(selectedSourcePersonId) || null : null;
  const quickEditPerson = selectedSourcePerson || playlistSelectedPerson || null;
  const previewPerson = useMemo(
    () => buildPlaylistRenderPerson(selectedPlaylistItem, playlistSelectedPerson),
    [playlistSelectedPerson, selectedPlaylistItem],
  );
  const genericLayers = useMemo(() => getVisibleGenericLayers(project), [project]);
  const programInOutro = useMemo(
    () => isProgramInOutro(live, playbackNow),
    [live, playbackNow],
  );
  const controlsReady = relay.connected && relay.hasSnapshot && !relay.connectionError;
  const canPlay = controlsReady && Boolean(selectedPlaylistItem);
  const canPause = controlsReady && live?.phase === LIVE_PHASE_PLAYING && Boolean(live?.program);
  const canEndOut = controlsReady && Boolean(live?.program) && !programInOutro;
  const canStop = controlsReady && Boolean(live?.program);

  useEffect(() => {
    if (!people.length) {
      setSelectedSourcePersonId(null);
      return;
    }
    if (selectedSourcePersonId && peopleById.has(selectedSourcePersonId)) return;
    setSelectedSourcePersonId(playlistSelectedPerson?.id || people[0]?.id || null);
  }, [people, peopleById, playlistSelectedPerson, selectedSourcePersonId]);

  useEffect(() => {
    if (!selectedPlaylistItem?.id) {
      lastPreviewItemIdRef.current = null;
      return;
    }
    if (lastPreviewItemIdRef.current === selectedPlaylistItem.id) return;
    lastPreviewItemIdRef.current = selectedPlaylistItem.id;
    setSelectedSourcePersonId(selectedPlaylistItem.personId);
  }, [selectedPlaylistItem]);

  useEffect(() => {
    setPlaybackNow(getRelayClockNow(relay.serverClockOffsetMs));
    if (live?.phase !== LIVE_PHASE_PLAYING || !live?.program) return undefined;
    const timerId = window.setInterval(() => {
      setPlaybackNow(getRelayClockNow(relay.serverClockOffsetMs));
    }, 100);
    return () => window.clearInterval(timerId);
  }, [live?.phase, live?.program, live?.playbackElapsedMs, live?.playbackStartedAt, relay.serverClockOffsetMs]);

  const sendPlaylistUpdate = useCallback((nextPlaylist) => {
    relay.send({ type: 'live:playlist', playlistItems: nextPlaylist });
  }, [relay]);

  const handleSelectPreviewItem = useCallback((playlistItemId) => {
    relay.send({ type: 'live:preview', playlistItemId });
    const selectedPlaylist = playlistItems.find((item) => item.id === playlistItemId);
    if (selectedPlaylist?.personId) setSelectedSourcePersonId(selectedPlaylist.personId);
  }, [playlistItems, relay]);

  const handlePlay = useCallback(() => {
    if (!controlsReady) return;
    relay.send({ type: 'live:play' });
  }, [controlsReady, relay]);

  const handlePlayPlaylistItem = useCallback((playlistItem) => {
    if (!playlistItem || !controlsReady) return;
    setSelectedSourcePersonId(playlistItem.personId);
    relay.send({ type: 'live:preview', playlistItemId: playlistItem.id });
    if (live?.program) relay.send({ type: 'live:stop' });
    relay.send({ type: 'live:play' });
  }, [controlsReady, live?.program, relay]);

  const handlePause = useCallback(() => {
    if (!controlsReady) return;
    relay.send({ type: 'live:pause' });
  }, [controlsReady, relay]);

  const handleEndOut = useCallback(() => {
    if (!controlsReady) return;
    relay.send({ type: 'live:endOut' });
  }, [controlsReady, relay]);

  const handleStop = useCallback(() => {
    if (!controlsReady) return;
    relay.send({ type: 'live:stop' });
  }, [controlsReady, relay]);

  const handleQuickEdit = useCallback((personId, field, value) => {
    if (!controlsReady || !personId) return;
    relay.send({ type: 'live:patchPerson', personId, changes: { [field]: value } });
  }, [controlsReady, relay]);

  const handleQuickGenericEdit = useCallback((personId, primitiveId, value, fallbackText) => {
    if (!controlsReady || !personId) return;
    const person = peopleById.get(personId);
    if (!person) return;
    const currentBuckets = person.genericTextByPresetId && typeof person.genericTextByPresetId === 'object'
      ? person.genericTextByPresetId
      : {};
    const bucket = currentBuckets[project.activePresetId] && typeof currentBuckets[project.activePresetId] === 'object'
      ? { ...currentBuckets[project.activePresetId] }
      : {};
    if (value === fallbackText) delete bucket[primitiveId];
    else bucket[primitiveId] = value;
    const nextBuckets = { ...currentBuckets };
    if (Object.keys(bucket).length) nextBuckets[project.activePresetId] = bucket;
    else delete nextBuckets[project.activePresetId];
    relay.send({ type: 'live:patchPerson', personId, changes: { genericTextByPresetId: nextBuckets } });
  }, [controlsReady, peopleById, project?.activePresetId, relay]);

  const handleToggleMirrored = useCallback((playlistItemId) => {
    if (!controlsReady || !playlistItemId) return;
    const playlistItem = playlistItems.find((item) => item.id === playlistItemId);
    if (!playlistItem) return;
    const person = peopleById.get(playlistItem.personId);
    if (!person) return;
    const nextMirrored = !getPlaylistItemEffectiveMirrored(playlistItem, person);
    sendPlaylistUpdate(playlistItems.map((item) => (
      item.id === playlistItemId ? { ...item, mirroredOverride: nextMirrored } : item
    )));
  }, [controlsReady, peopleById, playlistItems, sendPlaylistUpdate]);

  const handleCopyOutputUrl = useCallback(() => {
    navigator.clipboard?.writeText(outputUrl);
  }, [outputUrl]);

  const handleCopyControlUrl = useCallback(() => {
    navigator.clipboard?.writeText(controlUrl);
  }, [controlUrl]);

  const handleOutputUrlDragStart = useCallback((event) => {
    event.dataTransfer?.setData('text/plain', outputUrl);
    event.dataTransfer?.setData('text/uri-list', outputUrl);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyLink';
  }, [outputUrl]);

  const handleDropAtIndex = useCallback((targetIndex) => {
    if (!controlsReady || !draggedItem) return;
    if (draggedItem.kind === 'source' && draggedItem.personId) {
      const nextItem = createLivePlaylistItem(draggedItem.personId);
      const nextPlaylist = insertPlaylistItemAt(playlistItems, nextItem, targetIndex);
      sendPlaylistUpdate(nextPlaylist);
      relay.send({ type: 'live:preview', playlistItemId: nextItem.id });
      setSelectedSourcePersonId(draggedItem.personId);
      setDraggedItem(null);
      return;
    }
    if (draggedItem.kind === 'playlist' && draggedItem.playlistItemId) {
      sendPlaylistUpdate(movePlaylistItemToIndex(playlistItems, draggedItem.playlistItemId, targetIndex));
      setDraggedItem(null);
    }
  }, [controlsReady, draggedItem, playlistItems, relay, sendPlaylistUpdate]);

  const handleRemovePlaylistItem = useCallback((playlistItemId) => {
    if (!controlsReady) return;
    sendPlaylistUpdate(playlistItems.filter((item) => item.id !== playlistItemId));
    setDraggedItem(null);
  }, [controlsReady, playlistItems, sendPlaylistUpdate]);

  if (!roomId) {
    return renderEmptyState({
      title: 'Live room missing',
      body: 'Open this page from the editor by clicking Go Live! so the app can create a room first.',
    });
  }

  if (!project && relay.connectionError) {
    return renderEmptyState({
      title: 'Unable to join live room',
      body: getRelayErrorMessage(relay.connectionError),
    });
  }

  if (!project) {
    return renderEmptyState({
      title: 'Connecting to live room',
      body: 'Waiting for the relay to send this room state.',
    });
  }

  return (
    <div className="live-shell">
      <header className="live-topbar">
        <div className="live-branding">
          <h1>Go Live!</h1>
          <span className={`live-status-pill ${controlsReady ? 'connected' : 'disconnected'}`}>
            {controlsReady ? 'Relay connected' : 'Relay disconnected'}
          </span>
        </div>
        <div className="live-topbar-actions">
          <div className="live-output-url-group">
            <span className="live-output-url-help-wrap">
              <button
                type="button"
                className="live-output-url-help-trigger"
                aria-label={OUTPUT_URL_TOOLTIP}
                aria-describedby="live-output-url-tooltip"
              >
                <InfoCircleIcon className="live-output-url-help-icon" aria-hidden="true" focusable="false" />
              </button>
              <span id="live-output-url-tooltip" role="tooltip" className="live-output-url-help-tooltip">{OUTPUT_URL_TOOLTIP}</span>
            </span>
            <button
              type="button"
              className="secondary-btn live-output-url-button"
              draggable
              onClick={handleCopyOutputUrl}
              onDragStart={handleOutputUrlDragStart}
              title="Click to copy. You can also drag this into OBS."
            >
              Output URL
            </button>
          </div>
          <div className="live-output-url-group">
            <span className="live-output-url-help-wrap">
              <button
                type="button"
                className="live-output-url-help-trigger"
                aria-label={CONTROL_URL_TOOLTIP}
                aria-describedby="live-control-url-tooltip"
              >
                <InfoCircleIcon className="live-output-url-help-icon" aria-hidden="true" focusable="false" />
              </button>
              <span id="live-control-url-tooltip" role="tooltip" className="live-output-url-help-tooltip">{CONTROL_URL_TOOLTIP}</span>
            </span>
            <button type="button" className="secondary-btn live-output-url-button" onClick={handleCopyControlUrl} title="Copy private control link">
              Control URL
            </button>
          </div>
        </div>
      </header>

      <div className="live-meta-row">
        <span>Room: {roomId}</span>
        <span>Recommended browser source: {format.width}x{format.height}</span>
        <span>On Air: {live.phase}</span>
      </div>

      {relay.connectionError ? (
        <div className="live-help-strip live-help-strip-warning">
          <strong>Relay issue:</strong>
          <span>{getRelayErrorMessage(relay.connectionError)}</span>
        </div>
      ) : null}

      {!controlsReady ? (
        <div className="live-help-strip live-help-strip-warning">
          <strong>Relay offline:</strong>
          <span>Controls are read-only until this room reconnects to the Railway relay.</span>
        </div>
      ) : null}

      <div className="live-layout">
        <aside className="live-panel-card live-sources">
          <div className="live-panel-header">
            <h2>Sources</h2>
            <span>{people.length} items</span>
          </div>
          <div className="live-source-list">
            {people.map((person) => {
              const isSelected = selectedSourcePersonId === person.id;
              const isLinked = !isSelected && playlistSelectedPerson?.id === person.id;
              return (
                <button
                  key={person.id}
                  type="button"
                  className={`live-source-item ${isSelected ? 'is-selected' : ''} ${isLinked ? 'is-linked' : ''}`.trim()}
                  aria-pressed={isSelected}
                  draggable={controlsReady}
                  data-testid={`source-${person.id}`}
                  onClick={() => setSelectedSourcePersonId(person.id)}
                  onDragStart={() => controlsReady && setDraggedItem({ kind: 'source', personId: person.id })}
                  onDragEnd={() => setDraggedItem(null)}
                >
                  <strong>{person.name || 'Untitled'}</strong>
                  <span>{formatPersonSubtitle(person)}</span>
                </button>
              );
            })}
          </div>
          <div className="live-sources-footer">
            <button type="button" className="secondary-btn live-sources-footer-action" onClick={() => window.open(getEditorUrl(), '_self')}>
              Back to Editor
            </button>
          </div>
        </aside>

        <section className="live-center">
          <div
            className="live-panel-card live-playlist"
            data-testid="playlist-panel"
            onDragOver={(event) => controlsReady && event.preventDefault()}
            onDrop={() => handleDropAtIndex(playlistItems.length)}
          >
            <div className="live-panel-header">
              <h2>Playlist</h2>
              <span>{playlistItems.length} items</span>
            </div>

            {playlistItems.length ? (
              <div className="live-playlist-list">
                {playlistItems.map((item, index) => {
                  const person = peopleById.get(item.personId);
                  if (!person) return null;
                  const isSelected = live.previewItemId === item.id;
                  const effectiveMirrored = getPlaylistItemEffectiveMirrored(item, person);
                  return (
                    <div
                      key={item.id}
                      className={`live-playlist-row ${isSelected ? 'is-selected' : ''}`}
                      data-testid={`playlist-item-${item.id}`}
                      onDragOver={(event) => {
                        if (!controlsReady) return;
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleDropAtIndex(index);
                      }}
                    >
                      <button
                        type="button"
                        className="live-playlist-select"
                        aria-pressed={isSelected}
                        data-testid={`playlist-select-${item.id}`}
                        onClick={() => handleSelectPreviewItem(item.id)}
                        draggable={controlsReady}
                        onDragStart={() => controlsReady && setDraggedItem({ kind: 'playlist', playlistItemId: item.id })}
                        onDragEnd={() => setDraggedItem(null)}
                      >
                        <span className="live-playlist-index">{index + 1}</span>
                        <span className="live-playlist-copy">
                          <strong>{person.name || 'Untitled'}</strong>
                          <span>{formatPersonSubtitle(person)}</span>
                        </span>
                      </button>
                      <div className="live-playlist-actions">
                        <button
                          type="button"
                          className={`secondary-btn live-playlist-action live-playlist-mirror-btn ${effectiveMirrored ? 'is-active' : ''}`.trim()}
                          aria-label={`Toggle mirror for ${person.name || 'Untitled'}`}
                          aria-pressed={effectiveMirrored ? 'true' : 'false'}
                          title="Mirror"
                          data-testid={`mirror-${item.id}`}
                          onClick={() => handleToggleMirrored(item.id)}
                          disabled={!controlsReady}
                        >
                          <MirrorLeftIcon className="live-control-icon live-playlist-mirror-icon" />
                        </button>
                        <button
                          type="button"
                          className="export-btn primary live-playlist-action"
                          aria-label={`Play ${person.name || 'Untitled'}`}
                          title="Play"
                          data-testid={`play-item-${item.id}`}
                          onClick={() => handlePlayPlaylistItem(item)}
                          disabled={!controlsReady}
                        >
                          <PlayStreamIcon className="live-control-icon live-playlist-play-icon" aria-hidden="true" focusable="false" />
                        </button>
                        <button
                          type="button"
                          className="secondary-btn preset-delete-btn live-playlist-remove live-playlist-action"
                          aria-label={`Remove ${person.name || 'Untitled'} from playlist`}
                          title="Remove"
                          data-testid={`remove-${item.id}`}
                          onClick={() => handleRemovePlaylistItem(item.id)}
                          disabled={!controlsReady}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className="live-playlist-empty"
                data-testid="playlist-empty-dropzone"
                onDragOver={(event) => controlsReady && event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleDropAtIndex(0);
                }}
              >
                <strong>Playlist is empty</strong>
                <span>Drag a lower third from Sources to start the queue.</span>
              </div>
            )}

            {playlistItems.length ? (
              <div
                className="live-playlist-end-dropzone"
                data-testid="playlist-end-dropzone"
                onDragOver={(event) => controlsReady && event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleDropAtIndex(playlistItems.length);
                }}
              >
                Drop here to append to the playlist
              </div>
            ) : null}
          </div>

          <section className="live-panel-card live-playback-controls-card">
            <div className="live-playback-controls">
              <div className="live-playback-actions">
                <button
                  type="button"
                  className="export-btn primary live-icon-button"
                  aria-label="Play"
                  title="Play"
                  disabled={!canPlay}
                  onClick={handlePlay}
                >
                  <PlayStreamIcon className="live-control-icon live-control-icon-play" aria-hidden="true" focusable="false" />
                </button>
                <button
                  type="button"
                  className="secondary-btn live-icon-button"
                  aria-label="Pause"
                  title="Pause"
                  disabled={!canPause}
                  onClick={handlePause}
                >
                  <PauseIcon className="live-control-icon" aria-hidden="true" focusable="false" />
                </button>
                <button
                  type="button"
                  className="secondary-btn live-icon-button"
                  aria-label="End Out"
                  title="End Out"
                  disabled={!canEndOut}
                  onClick={handleEndOut}
                >
                  <MediaBackwardEndIcon className="live-control-icon" aria-hidden="true" focusable="false" />
                </button>
                <button
                  type="button"
                  className="secondary-btn live-icon-button"
                  aria-label="Stop"
                  title="Stop"
                  disabled={!canStop}
                  onClick={handleStop}
                >
                  <StopIcon className="live-control-icon" aria-hidden="true" focusable="false" />
                </button>
              </div>
            </div>
          </section>
        </section>

        <aside className="live-sidebar">
          <section className="live-panel-card live-quick-edit">
            <div className="live-panel-header">
              <h2>Quick Edit</h2>
              <span>{buildSelectedSubtitle(quickEditPerson)}</span>
            </div>
            {quickEditPerson ? (
              <div className="live-form-grid">
                <label>
                  <span>Name</span>
                  <input value={quickEditPerson.name} onChange={(event) => handleQuickEdit(quickEditPerson.id, 'name', event.target.value)} disabled={!controlsReady} />
                </label>
                <label>
                  <span>Title</span>
                  <input value={quickEditPerson.title} onChange={(event) => handleQuickEdit(quickEditPerson.id, 'title', event.target.value)} disabled={!controlsReady} />
                </label>
                {genericLayers.map((layer) => (
                  <label key={layer.primitiveId}>
                    <span>{layer.label}</span>
                    <input
                      value={resolveGenericTextValue({
                        person: quickEditPerson,
                        activePresetId: project.activePresetId,
                        primitiveId: layer.primitiveId,
                        fallbackText: layer.fallbackText,
                      })}
                      onChange={(event) => handleQuickGenericEdit(quickEditPerson.id, layer.primitiveId, event.target.value, layer.fallbackText)}
                      disabled={!controlsReady}
                    />
                  </label>
                ))}
              </div>
            ) : (
              <p className="muted-text">Select a source to edit it.</p>
            )}
          </section>
          <div className="live-sidebar-monitors">
            <PlaybackCanvas
              className="live-playback-monitor live-sidebar-monitor"
              label="Preview / Next"
              person={previewPerson}
              config={project.config}
              fps={project.fps}
              activePresetId={project.activePresetId}
              aspectRatioId={project.aspectRatioId}
              loopPreview
              pauseWhenHidden
              compact
            />
            <PlaybackCanvas
              className="live-playback-monitor live-sidebar-monitor"
              label="Program / On Air"
              liveProgram={live.program}
              livePhase={live.phase}
              playbackStartedAt={live.playbackStartedAt}
              playbackElapsedMs={live.playbackElapsedMs}
              serverClockOffsetMs={relay.serverClockOffsetMs}
              forceTransparentBackground
              compact
            />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default LiveControlApp;
